<script lang="ts">
  import { MaterialName, type StickerPreviewLayerProps } from '../types';
  import {
    centerSVGChildElement,
    centerSVGChildElementAndCounteractAnimationsTransform,
  } from '../svg-utils';

  let {
    selectedMaterial,
    settings = $bindable(),
    uniqueId,
    basePlateLayerStyle = $bindable(),
    printLayerStyle = $bindable(),
  }: StickerPreviewLayerProps = $props();

  let printLayer: SVGUseElement;
  let basePlateLayer: SVGUseElement;
  let holographicLayer: SVGUseElement | undefined = $state();
  let glitterLayer: SVGUseElement | undefined = $state();
  let prismaticLayer: SVGUseElement | undefined = $state();
  let metallicLayer: SVGUseElement | undefined = $state();
  let brushedLayer: SVGUseElement | undefined = $state();
  let laminateLayer: SVGUseElement | undefined = $state();
  let recycledLayer: SVGUseElement | undefined = $state();
  let printRef: SVGUseElement | undefined = $state();
  let clippyRef: SVGUseElement | undefined = $state();
  let cutlineRef: SVGUseElement | undefined = $state();
  let laminateOpacity = $derived(selectedMaterial.laminateSettings.laminateOpacity ?? 0);

  $effect(() => {
    let svg = document.querySelector(`#canvas-${uniqueId}`) as SVGSVGElement;
    /*     console.log('effect in layers', sticker); */
    if (svg) {
      if (holographicLayer) {
        centerSVGChildElementAndCounteractAnimationsTransform(svg, holographicLayer);
      }
      if (glitterLayer) {
        centerSVGChildElementAndCounteractAnimationsTransform(svg, glitterLayer);
      }
      if (metallicLayer) {
        centerSVGChildElementAndCounteractAnimationsTransform(svg, metallicLayer);
      }
      if (laminateLayer) {
        centerSVGChildElementAndCounteractAnimationsTransform(svg, laminateLayer);
      }
      if (basePlateLayer) {
        centerSVGChildElementAndCounteractAnimationsTransform(svg, basePlateLayer);
      }

      let clippySvgPath = document.querySelector(`#clippySvgPath-${uniqueId}`);
      clippySvgPath?.removeAttribute('transform');
      if (clippySvgPath && clippyRef) {
        clippyRef?.removeAttribute('transform');
        let transformRef = centerSVGChildElement(svg, clippyRef);
        clippySvgPath.setAttribute('transform', transformRef);
      }
      if (cutlineRef) {
        centerSVGChildElement(svg, cutlineRef);
      }
    }
  });
  let clipPath = $derived(settings.adminPreview ? undefined : `url(#clippy-${uniqueId})`);
</script>

<use opacity="0" bind:this={clippyRef} href="#clippySvgPath-{uniqueId}"></use>

<g
  class={selectedMaterial.type}
  class:admin={settings.adminPreview}
  clip-path={clipPath}
  style="z-index: -1; fill: white;"
>
  <g
    id="effectLayers-{uniqueId}"
    class:hidden={settings.adminPreview || !settings.effectLayerEnabled}
    style="mask: url(#inverted-whitelayer-mask-{uniqueId})"
  >
    <!--  -->
    <use
      bind:this={basePlateLayer}
      id="basePlateLayer-{uniqueId}"
      href="#baseplate-{uniqueId}"
      class:effectLayerDisabled={!settings.effectLayerEnabled}
      class="basePlateLayer"
      style={basePlateLayerStyle}
    ></use>
    {#if selectedMaterial.type === MaterialName.Holographic}
      <use
        bind:this={holographicLayer}
        id="holographicLayer-{uniqueId}"
        href="#holographicLayerDef-{uniqueId}"
        class="holographicLayer"
      />
    {:else if selectedMaterial.type === MaterialName.Glitter || selectedMaterial.type === MaterialName.Pixie}
      <g mask="url(#glitterMask-{uniqueId})">
        <use
          bind:this={glitterLayer}
          id="glitterLayer-{uniqueId}"
          href="#glitterLayerDef-{uniqueId}"
          class="glitterLayer"
        />
      </g>
    {:else if selectedMaterial.type === MaterialName.Prismatic}
      <use class="prismaticLayer" bind:this={prismaticLayer} href="#prismaticLayerDef-{uniqueId}" />
    {/if}
    <!-- Metallic Shine Image Layer. (Same as glossy laminate?) -->
    {#if selectedMaterial.isMetal}
      <use
        bind:this={metallicLayer}
        id="metallicLayer-{uniqueId}"
        href="#metallicLayerDef-{uniqueId}"
        class="metallicLayer"
      />
    {/if}

    <!-- Brushed Aluminum  -->
    {#if selectedMaterial.type === MaterialName.Brushed}
      <use
        bind:this={brushedLayer}
        id="brushedLayer-{uniqueId}"
        href="#brushedLayerDef-{uniqueId}"
        opacity=".3"
        class="brushedLayer"
      ></use>
    {/if}
    <!-- Reflective  -->
    {#if selectedMaterial.type === MaterialName.Reflective}
      <use id="reflectiveLayer-{uniqueId}" href="#reflectiveLayerDef-{uniqueId}"></use>
    {/if}
  </g>
  <!-- Print Image Layer With printimage, bleed, and whitelayer-->
  <use
    bind:this={printLayer}
    id="printLayer-{uniqueId}"
    href="#imageGroup-{uniqueId}"
    class="printLayer"
    style={printLayerStyle}
  ></use>
  <!-- PrintRef for getting bbox of image -->
  <use
    bind:this={printRef}
    id="printref-{uniqueId}"
    href="#printImage-{uniqueId}"
    style="opacity:0;"
  ></use>
  <!-- Laminate Image Layer TODO: Why is 0.01 opacity if its disabled? -->
  {#if settings.laminateLayerEnabled}
    <!-- No displacement filter for safari / mobile to save performance -->
    {#if settings.lightweight}
      <!-- TODO: Implement predistorted laminate image lightweight? -->
      <use
        bind:this={laminateLayer}
        id="laminateLayer-{uniqueId}"
        class="laminateLayer"
        href="#laminateLayerDef-{uniqueId}"
        opacity={selectedMaterial.laminateSettings.enabled ? laminateOpacity : 0.01}
      ></use>
    {:else}
      <use
        bind:this={laminateLayer}
        id="laminateLayer-{uniqueId}"
        class="laminateLayer"
        href="#laminateLayerDef-{uniqueId}"
        filter="url(#displacementFilter-{uniqueId})"
        opacity={selectedMaterial.laminateSettings.enabled ? laminateOpacity : 0.01}
      ></use>
    {/if}
  {/if}

  <!-- Kraft Paper -->
  {#if selectedMaterial.type === MaterialName.Recycled}
    <use
      bind:this={recycledLayer}
      id="recycled-extra-{uniqueId}"
      opacity=".2"
      href="#recycledLayerDef-{uniqueId}"
      class="recycled-extra"
    />
  {/if}
</g>
{#if settings.adminPreview}
  <use
    fill-opacity="0"
    opacity="1"
    stroke-width="1"
    stroke="magenta"
    bind:this={cutlineRef}
    href="#clippySvgPath-{uniqueId}"
  ></use>
{/if}

<style>
  .recycled-extra {
    pointer-events: none;
    filter: saturate(0) contrast(3);
    opacity: 0.2;
  }

  /* if class admin is present the materiallayers and shines should be invisible */
  .admin .glitterLayer,
  .admin .holographicLayer,
  .admin .prismaticLayer,
  .admin .metallicLayer,
  .admin .brushedLayer,
  .admin .laminateLayer,
  .admin .recycled-extra {
    display: none;
  }
  .admin .basePlateLayer {
    fill: none;
  }
</style>
